import {
    type FC,
    type ReactNode,
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
    useContext,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import {
    ScrollCarouselProvider,
    useScrollCarousel,
} from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { InnerScrollCarousel } from 'dibs-scroll-carousel/exports/InnerScrollCarousel.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { useItemMediaItems } from 'dibs-image-utils/exports/useItemMediaItems.ts';
import videoTracking from '../../shared/PdpSharedVimeoVideo/tracking.ts';

import { HeroCarouselThumb } from './HeroCarouselThumb.tsx';
import { HeroCarouselImage } from './HeroCarouselImage.tsx';
import { HeroCarouselCounter } from './HeroCarouselCounter.tsx';
import { ViewInRoom } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/ViewInRoom.tsx';
import { HeroCarouselRequestMore } from './HeroCarouselRequestMore.tsx';
import { ZoomModal } from './ZoomModal.tsx';
import { ThumbScrollArrow } from './ThumbScrollArrow.tsx';
import { CarouselItemWrapper } from './CarouselItemWrapper.tsx';
import { PreviewOverlay } from './PreviewOverlay.tsx';
import { VideoOverlay, useVideoOverlay } from './VideoOverlay.tsx';
import { useThumbsPanel } from './useThumbsPanel.ts';
import { BrowserHistoryContext } from '../../../global/BrowserHistoryContext/BrowserHistoryContext.jsx';
import { isPhoto, type CarouselItem } from './types.ts';
import { THUMBS_VISIBLE } from './useThumbsPanel.ts';

import { type PdpRespHeroCarousel_item$key } from './__generated__/PdpRespHeroCarousel_item.graphql.ts';
import { type PdpRespHeroCarousel_viewer$key } from './__generated__/PdpRespHeroCarousel_viewer.graphql.ts';
import { type PdpRespHeroCarousel_user$key } from './__generated__/PdpRespHeroCarousel_user.graphql.ts';

import styles from './PdpRespHeroCarousel.scss';

const viewerFragment = graphql`
    fragment PdpRespHeroCarousel_viewer on Viewer {
        ...useItemMediaItems_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespHeroCarousel_item on Item {
        ...useItemMediaItems_item
        ...HeroCarouselImage_item
        ...ViewInRoom_item
        ...HeroCarouselRequestMore_item
        sortedPhotos {
            ...useItemMediaItems_sortedPhotos
            ...HeroCarouselThumb_sortedPhoto
            ...HeroCarouselImage_sortedPhoto
            __typename
            masterOrZoomPath
            largePath
            alt
        }
        videoVimeo {
            ...useItemMediaItems_videoVimeo
            ...HeroCarouselThumb_videoVimeo
            ...VimeoVideo_videoVimeo
            thumbnail(size: w100h75, includePlayButton: false, pad: false)
            previewThumbnail: thumbnail(size: w960h540, includePlayButton: false, pad: false)
            vimeoId
        }
        viewInRoomPhotos {
            ...useItemMediaItems_viewInRoomPhotos
            ...HeroCarouselThumb_viewInRoomPhoto
            ...ViewInRoom_viewInRoomPhoto
        }
        hasPrivateListing
        seller {
            isAccessible
        }
        serviceId
        title
        firstPhotoWebPath(size: small)
    }
`;

const userFragment = graphql`
    fragment PdpRespHeroCarousel_user on User {
        ...ViewInRoom_user
        ...HeroCarouselRequestMore_user
        serviceId
    }
`;

const PdpRespHeroCarouselInner: FC<{
    item: PdpRespHeroCarousel_item$key;
    user: PdpRespHeroCarousel_user$key | null | undefined;
    carouselItems: CarouselItem[];
    currency: string;
    disableModals?: boolean;
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    imageOverlay?: ReactNode;
    children?: ReactNode;
}> = ({
    item: itemRef,
    user: userRef,
    carouselItems,
    currency,
    disableModals,
    afterDisplay,
    selectedSkuId,
    imageOverlay,
    children,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const userId = user?.serviceId || null;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const { currentState, pushState, back }: $TSFixMe = useContext(BrowserHistoryContext);

    useEffect(() => {
        const currentZoomModalOpen = !!(currentState && currentState.zoomModalOpen);
        if (currentZoomModalOpen !== isModalOpen) {
            setIsModalOpen(currentZoomModalOpen);
        }
    }, [currentState, isModalOpen]);

    const openZoomModal = useCallback(() => {
        if (disableModals) {
            return;
        }
        pushState({ zoomModalOpen: true }, '#zoomModalOpen');
        setIsModalOpen(true);
    }, [disableModals, pushState]);

    const closeZoomModal = useCallback(() => {
        if (isModalOpen) {
            back();
        }
        setIsModalOpen(false);
    }, [isModalOpen, back]);

    const { activeSlide, totalSlides, resetCurrentIndex } = useScrollCarousel();

    const selectedSkuIdRef = useRef(selectedSkuId);
    useEffect(() => {
        if (selectedSkuIdRef.current !== selectedSkuId) {
            selectedSkuIdRef.current = selectedSkuId;
            resetCurrentIndex();
        }
    }, [resetCurrentIndex, selectedSkuId]);

    const zoomMedia = useMemo(() => {
        return carouselItems
            .map(carouselItem => {
                if (carouselItem.__type === 'photo') {
                    return {
                        src: carouselItem.masterOrZoomPath,
                        alt: carouselItem.alt,
                    };
                } else if (carouselItem.__type === 'video') {
                    return {
                        type: 'VIMEO',
                        src: carouselItem.thumbnail,
                        thumbnail: carouselItem.thumbnail,
                        vimeoPlayerOptions: {
                            quality: '1080p',
                            id: carouselItem.vimeoId,
                            tracking: videoTracking,
                        },
                    };
                }
                return null;
            })
            .filter(filterNulls);
    }, [carouselItems]);

    const {
        thumbsScrollRef,
        canScrollUp,
        canScrollDown,
        scrollThumbsUp,
        scrollThumbsDown,
        hasOverflow,
        previewImage,
        previewIndex,
        handleThumbHover,
        handleThumbHoverEnd,
        handleThumbSelect,
        onArrowClick,
    } = useThumbsPanel({ carouselItems, afterDisplay });

    const video = useVideoOverlay({ carouselItems, activeSlide, previewIndex });

    return (
        <>
            <ZoomModal
                isOpen={isModalOpen}
                onCloseClick={closeZoomModal}
                zoomMedia={zoomMedia}
                totalItems={carouselItems.length}
                title={item.title}
                image={item.firstPhotoWebPath}
                itemId={item.serviceId}
                userId={userId}
                currency={currency}
            />

            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.wFull,
                    dibsCss.gapSmall,
                    dibsCss.m0,
                    dibsCss.p0
                )}
            >
                {/* Thumbs column */}
                <div className={classnames(dibsCss.relative, dibsCss.shrink0, styles.thumbsColumn)}>
                    {afterDisplay && (
                        <>
                            {hasOverflow && canScrollUp && (
                                <ThumbScrollArrow direction="up" onClick={scrollThumbsUp} />
                            )}
                            <div
                                className={classnames(
                                    dibsCss.hFull,
                                    dibsCss.overflowYAuto,
                                    dibsCss.overflowXHidden,
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.gapXsmall,
                                    styles.thumbsList
                                )}
                                ref={thumbsScrollRef}
                                onMouseLeave={handleThumbHoverEnd}
                            >
                                {carouselItems.map((carouselItem, index) => {
                                    const commonProps = {
                                        index,
                                        isActive: index === activeSlide - 1,
                                        totalSlides,
                                        onSelect: handleThumbSelect,
                                        onHover: handleThumbHover,
                                    } as const;

                                    if (carouselItem.__type === 'photo') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={carouselItem}
                                                videoVimeo={null}
                                                viewInRoomPhoto={null}
                                                {...commonProps}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'video') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={null}
                                                videoVimeo={carouselItem}
                                                viewInRoomPhoto={null}
                                                {...commonProps}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'viewInRoom') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                viewInRoomPhoto={carouselItem}
                                                {...commonProps}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'requestMore') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                viewInRoomPhoto={null}
                                                {...commonProps}
                                            />
                                        );
                                    }
                                    return <div key={index} />;
                                })}
                            </div>
                            {hasOverflow && canScrollDown && (
                                <ThumbScrollArrow direction="down" onClick={scrollThumbsDown} />
                            )}
                        </>
                    )}
                </div>

                {/* Main carousel */}
                <div
                    className={classnames(
                        dibsCss.flex1,
                        dibsCss.minW0,
                        dibsCss.flex,
                        dibsCss.flexCol
                    )}
                >
                    <div
                        className={classnames(
                            dibsCss.relative,
                            dibsCss.roundedXxsmall,
                            dibsCss.overflowHidden,
                            styles.displayContainer
                        )}
                    >
                        <InnerScrollCarousel
                            showArrows
                            orientation="horizontal"
                            itemsToShow={1}
                            animation="none"
                            onArrowClick={onArrowClick}
                            dataTn="pdp-image-carousel"
                            isFullWidth
                            arrowClassName={classnames(
                                dibsCss.zLow,
                                dibsCss.top50,
                                styles.mainArrow
                            )}
                        >
                            {carouselItems.map((carouselItem, index) => {
                                let child = null;
                                if (carouselItem.__type === 'photo') {
                                    child = (
                                        <HeroCarouselImage
                                            eagerLoad={index === 0}
                                            item={item}
                                            sortedPhoto={carouselItem}
                                        />
                                    );
                                } else if (
                                    carouselItem.__type === 'viewInRoom' &&
                                    isPhoto(carouselItems[0])
                                ) {
                                    child = (
                                        <ViewInRoom
                                            user={user}
                                            item={item}
                                            viewInRoomPhoto={carouselItem}
                                            mainImagePath={{
                                                imagePath: carouselItems[0].largePath,
                                                masterOrZoomPath: carouselItems[0].masterOrZoomPath,
                                            }}
                                        />
                                    );
                                } else if (carouselItem.__type === 'requestMore') {
                                    child = (
                                        <HeroCarouselRequestMore
                                            user={user}
                                            item={item}
                                            currency={currency}
                                            disableRequestMoreImagesModal={disableModals}
                                        />
                                    );
                                }

                                return (
                                    <CarouselItemWrapper
                                        key={index}
                                        type={carouselItem.__type}
                                        openZoomModal={openZoomModal}
                                        dataTn={`pdp-image-carousel-image-${index + 1}`}
                                    >
                                        {child}
                                    </CarouselItemWrapper>
                                );
                            })}
                        </InnerScrollCarousel>

                        <PreviewOverlay
                            previewImage={previewImage}
                            previewIndex={previewIndex}
                            carouselItems={carouselItems}
                            user={user}
                            item={item}
                            currency={currency}
                            disableModals={disableModals}
                        />

                        <VideoOverlay {...video} />

                        <HeroCarouselCounter />

                        {imageOverlay && (
                            <div
                                className={classnames(
                                    dibsCss.absolute,
                                    dibsCss.topXsmall,
                                    dibsCss.rightXsmall,
                                    dibsCss.zBump,
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.gapXsmall
                                )}
                            >
                                {imageOverlay}
                            </div>
                        )}
                    </div>
                    {children && (
                        <div className={classnames(dibsCss.flex, dibsCss.justifyCenter)}>
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

type Props = {
    item: PdpRespHeroCarousel_item$key;
    user: PdpRespHeroCarousel_user$key | null | undefined;
    viewer: PdpRespHeroCarousel_viewer$key;
    currency: string;
    disableModals?: boolean;
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    imageOverlay?: ReactNode;
    children?: ReactNode;
};

export const PdpRespHeroCarousel: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user,
    currency,
    disableModals,
    afterDisplay,
    selectedSkuId,
    imageOverlay,
    children,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const { videoVimeo, hasPrivateListing, seller } = item;

    const sortedPhotos = (item.sortedPhotos || []).filter(filterNulls);
    const viewInRoomPhotos = (item.viewInRoomPhotos || []).filter(filterNulls);
    const mediaItems = useItemMediaItems({
        viewer,
        item,
        sortedPhotos,
        videoVimeo,
        viewInRoomPhotos,
        selectedSkuId,
    });

    const showRequestMore = !hasPrivateListing && seller?.isAccessible;
    const carouselItems: CarouselItem[] = useMemo(() => {
        return showRequestMore ? [...mediaItems, { __type: 'requestMore' }] : mediaItems;
    }, [mediaItems, showRequestMore]);

    const mainPhoto = sortedPhotos?.[0];

    return (
        <>
            <div className={styles.printHide}>
                <ScrollCarouselProvider
                    isInfinite
                    totalItems={carouselItems.length}
                    largestItemsToShow={THUMBS_VISIBLE}
                    stepSize={1}
                >
                    <PdpRespHeroCarouselInner
                        item={itemRef}
                        user={user}
                        carouselItems={carouselItems}
                        currency={currency}
                        disableModals={disableModals}
                        afterDisplay={afterDisplay}
                        selectedSkuId={selectedSkuId}
                        imageOverlay={imageOverlay}
                    >
                        {children}
                    </PdpRespHeroCarouselInner>
                </ScrollCarouselProvider>
            </div>

            {/* Print view */}
            {afterDisplay && mainPhoto?.alt && mainPhoto?.masterOrZoomPath && (
                <div className={styles.printShow}>
                    <img
                        src={mainPhoto.masterOrZoomPath}
                        alt={mainPhoto.alt}
                        className={classnames(
                            dibsCss.block,
                            dibsCss.wFull,
                            dibsCss.objectScaleDown,
                            styles.printImage
                        )}
                    />
                </div>
            )}
        </>
    );
};
